import React from "react";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { observer } from "mobx-react-lite";
import classnames from "classnames";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import styles from "./AuthorizedAction.module.scss";
import { useSearchParams } from "react-router-dom";

type PermissionProps =
  | { permission: UserPermissions; optionalPermissions?: never }
  | { permission?: never; optionalPermissions: UserPermissions[] }
  | { permission?: never; optionalPermissions?: never };

export type AuthorizedActionProps = {
  children: React.ReactNode;
  mode?: "hide" | "disable";
  adminOnly?: boolean;
  testId?: string;
  /**
   * Class for the wrapper div this renders.
   *
   * Without it a caller cannot style the wrapper at all, and one that groups several actions
   * under a single permission check has no way to lay them out: the wrapper becomes the only
   * child of the caller's row, so the row's flex lays out the wrapper while the actions inside
   * it stack. The alternative was reaching in with a `> div` element selector, which is
   * invisible from the call site.
   *
   * Applied to every branch, so layout does not change when the user turns out to be
   * unauthorized.
   */
  className?: string;
} & PermissionProps;

export const AuthorizedAction: React.FC<AuthorizedActionProps> = observer(
  ({ children, permission, optionalPermissions, mode = "disable", adminOnly = false, testId, className }) => {
    const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
    const [searchParams] = useSearchParams();
    const currentOrgId = searchParams.get("orgId") || userStore.user?.organizationId;
    const isAdmin = userStore.isAdmin || userStore.isSuperAdmin;
    const isSameOrg = Boolean(userStore.user && currentOrgId === userStore.user.organizationId);
    const hasUpdateAccessToOrg = userStore.hasUpdateAccessToOrg ?? false;
    const checkPermission = (): boolean => {
      if (permission) {
        return hasPermission(userStore.user, permission) && (isSameOrg || hasUpdateAccessToOrg);
      }
      if (optionalPermissions) {
        return Boolean(userStore.user && optionalPermissions.some((p) => hasPermission(userStore.user, p)) && (isSameOrg || hasUpdateAccessToOrg));
      }
      return isSameOrg || hasUpdateAccessToOrg;
    };

    const isAuthorized = isAdmin || (!adminOnly && checkPermission());
    const defaultTestId = "authorized-action-wrapper";
    const wrapperTestId = testId ?? defaultTestId;

    if (mode === "hide" && !isAuthorized) return <div className={classnames(styles.hidden, className)} data-testid={wrapperTestId} />;
    if (mode === "disable" && !isAuthorized)
      return (
        <div className={classnames(styles.disabled, className)} data-testid={wrapperTestId}>
          {children}
        </div>
      );
    return (
      <div className={className} data-testid={wrapperTestId}>
        {children}
      </div>
    );
  }
);
